import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { fetchSubmissionDetail } from '../../api/inventory';
import type { SubmissionDetail } from '../../types/shared';

import AuditLogTimeline from './AuditLogTimeline';
import { periodLabel } from './ClosePeriodSelector';

interface Props {
  submissionId: string;
  onClose: () => void;
}

// eslint-disable-next-line max-lines-per-function -- pre-existing length; BON-165 only adds drawer-animation classes, no new logic
export default function SubmissionDetailDrawer({ submissionId, onClose }: Props) {
  const [data, setData] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSubmissionDetail(submissionId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load submission.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="nb-drawer-scrim-enter absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="nb-drawer-enter relative w-full max-w-xl h-full bg-white dark:bg-[#1e2521] shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 dark:border-stone-700">
          <h2 className="text-base font-semibold text-stone-900 dark:text-stone-100">
            Submission detail
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition"
          >
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="p-5 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-12 rounded-lg bg-stone-100 dark:bg-stone-800 animate-pulse"
              />
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="p-5 text-sm text-red-600 dark:text-red-400">{error}</div>
        )}

        {data && !loading && (
          <div className="p-5 space-y-6">
            {/* Summary */}
            <section className="space-y-1">
              <div className="text-sm font-medium text-stone-900 dark:text-stone-100">
                {data.warehouseName}
              </div>
              <div className="text-xs text-stone-500 dark:text-stone-400">
                Period{' '}
                <span className="font-medium text-stone-700 dark:text-stone-300">
                  {periodLabel(data.periodMonth)}
                </span>
                <span className="mx-1">·</span>
                Counted on{' '}
                <span className="font-medium text-stone-700 dark:text-stone-300">
                  {data.inventoryDate}
                </span>
                <span className="mx-1">·</span>
                Submitted via <span className="font-mono">{data.source}</span>
              </div>
              {data.isEmpty && (
                <div className="mt-2 inline-flex items-center text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  Marked &ldquo;nothing to submit&rdquo;
                </div>
              )}
              {data.notes && (
                <div className="mt-2 text-sm text-stone-700 dark:text-stone-300 italic">
                  &ldquo;{data.notes}&rdquo;
                </div>
              )}
            </section>

            {/* Line items */}
            {!data.isEmpty && (
              <section>
                <h3 className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                  Line items ({data.items.length})
                </h3>
                {data.items.length === 0 ? (
                  <div className="text-sm text-stone-400 dark:text-stone-500">
                    No line items recorded.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-stone-500 dark:text-stone-400 border-b border-stone-200 dark:border-stone-700">
                        <th className="text-left py-1.5 pr-2 font-medium">Item</th>
                        <th className="text-left py-1.5 pr-2 font-medium">Category</th>
                        <th className="text-right py-1.5 font-medium">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((i) => (
                        <tr
                          key={i.inventoryItemId}
                          className="border-b border-stone-100 dark:border-stone-800 last:border-0"
                        >
                          <td className="py-1.5 pr-2 text-stone-800 dark:text-stone-200">
                            {i.name}
                          </td>
                          <td className="py-1.5 pr-2 text-xs text-stone-500 dark:text-stone-400">
                            {i.category}
                          </td>
                          <td className="py-1.5 text-right tabular-nums text-stone-800 dark:text-stone-200">
                            {i.quantity.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}

            {/* Audit timeline */}
            <section>
              <h3 className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">
                History
              </h3>
              <AuditLogTimeline events={data.auditEvents} periodApproval={data.periodApproval} />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
